import { useState, useRef, useEffect } from 'react'
import { Terminal, ChevronUp, ChevronDown, Trash2, Copy, CheckCircle } from 'lucide-react'
import './ExecutionLogPanel.css'

/**
 * ExecutionLogPanel - Terminal-style log output for workflow execution
 *
 * Displays real-time execution logs with timestamps and status indicators.
 * Collapsible panel that sits at the bottom of the designer view.
 */
function ExecutionLogPanel({ logs = [], onClear }) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [copied, setCopied] = useState(false)
  const logContainerRef = useRef(null)

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (logContainerRef.current && !isCollapsed) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight
    }
  }, [logs, isCollapsed])

  // Auto-expand when workflow starts running
  useEffect(() => {
    if (logs.length > 0 && isCollapsed) {
      const lastLog = logs[logs.length - 1]
      if (lastLog.type === 'start') {
        setIsCollapsed(false)
      }
    }
  }, [logs, isCollapsed])

  const handleCopy = async () => {
    const logText = logs
      .map(log => `[${log.timestamp}] ${log.message}`)
      .join('\n')

    try {
      await navigator.clipboard.writeText(logText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy logs:', err)
    }
  }

  const formatTimestamp = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  return (
    <div className={`execution-log-panel ${isCollapsed ? 'collapsed' : ''}`}>
      {/* Header bar */}
      <div className="log-panel-header" onClick={() => setIsCollapsed(!isCollapsed)}>
        <div className="log-panel-title">
          <Terminal size={14} />
          <span>Execution Log</span>
          {logs.length > 0 && (
            <span className="log-count">{logs.length}</span>
          )}
        </div>
        <div className="log-panel-actions">
          {!isCollapsed && (
            <>
              <button
                className="log-action-btn"
                onClick={(e) => { e.stopPropagation(); handleCopy() }}
                title="Copy logs"
              >
                {copied ? <CheckCircle size={14} /> : <Copy size={14} />}
              </button>
              <button
                className="log-action-btn"
                onClick={(e) => { e.stopPropagation(); onClear?.() }}
                title="Clear logs"
              >
                <Trash2 size={14} />
              </button>
            </>
          )}
          <button className="log-toggle-btn" title={isCollapsed ? 'Expand' : 'Collapse'}>
            {isCollapsed ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Log content */}
      {!isCollapsed && (
        <div className="log-panel-content" ref={logContainerRef}>
          {logs.length === 0 ? (
            <div className="log-empty">
              <span>No execution logs. Click Run to start a workflow.</span>
            </div>
          ) : (
            logs.map((log, index) => (
              <div key={index} className={`log-entry log-${log.level || 'info'}`}>
                <span className="log-timestamp">[{formatTimestamp(log.timestamp)}]</span>
                {log.node && <span className="log-node">[{log.node}]</span>}
                <span className="log-message">{log.message}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default ExecutionLogPanel
